import Button from '@/components/Button.jsx';

import { MdMemory } from '@/components/react-icons/main';
import confirmDialog from '@/lib/confirmDialog.js';
import { AddIcon } from '@/lib/constants/icons.js';
import debounce from '@/lib/debounce.js';
import sendToast from '@/lib/sendToast.js';
import stepActions from '@/screens/workflows/redux/actions/stepActions.js';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors.js';
import { randomId } from '@shared/utils/randomId.js';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import SliderClose from './SliderClose.jsx';
import SliderHeader from './SliderHeader.jsx';
import SliderTitle from './SliderTitle.jsx';
import MemorySliderItem from './MemorySliderItem.jsx';

const MemoriesSlider = ({ onClose, disabled = false }) => {
  const memories = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state)?.options?.memory) || [];
  const [localMemories, setLocalMemories] = useState(memories);

  useEffect(() => {
    setLocalMemories(memories);
  }, [memories]);

  const getLabel = (value, options) => {
    const option = options.find((o) => o.value === value);
    return option?.label || value;
  };

  const updateMemory = useCallback(
    debounce((memoryId, updates) => {
      stepActions.set('options.memory', (options) => (Array.isArray(options) ? options : []).map((option) => (option.id === memoryId ? { ...option, ...updates } : option)));
    }, 500),
    []
  );

  const handleChange = (memoryId, field, value) => {
    const memory = localMemories.find((m) => m.id === memoryId);
    if (field !== 'key' && !memory.key) {
      sendToast({ message: 'Please enter a key for this memory', type: 'error' });
      return;
    }
    const updates = { [field]: value };
    setLocalMemories((prev) => prev.map((m) => (m.id === memoryId ? { ...m, ...updates } : m)));
    updateMemory(memoryId, updates);
  };

  const handleDelete = async (memoryId) => {
    const confirmed = await confirmDialog({
      title: 'Delete Memory',
      message: 'Are you sure you want to delete this memory?',
      confirm: 'Delete'
    });

    if (confirmed) {
      stepActions.set('options.memory', (options) => (Array.isArray(options) ? options : []).filter((option) => option.id !== memoryId));
    }
  };

  const handleAdd = () => {
    const newMemory = {
      id: randomId(),
      key: '',
      name: '',
      savesOn: 'success',
      expiresOn: 'never',
      value: ''
    };
    stepActions.set('options.memory', (options) => [...(Array.isArray(options) ? options : []), newMemory]);
  };

  return (
    <>
      <SliderHeader onClose={onClose} disabled={disabled} />
      <SliderTitle icon={MdMemory} title="Edit Memory Entries" description="Configure memory settings for all entries." helpIconContext="workflowStepMemoryContext" />

      {localMemories.map((memory) => (
        <MemorySliderItem key={memory.id} memory={memory} handleDelete={handleDelete} handleChange={handleChange} getLabel={getLabel} disabled={disabled} />
      ))}
      <Button className="full-width" size="xl" icon={AddIcon} onClick={handleAdd} disabled={disabled}>
        Add Memory Entry
      </Button>
      <SliderClose onClose={onClose} />
    </>
  );
};

export default MemoriesSlider;
